import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";
import {
  setBrandsData,
  setBrandsLoading,
  setBrandsError,
  setBrandsMonthlyData,
  setBrandsMonthlyLoading,
  setBrandsMonthlyError,
  setBrandsReportAttemptData,
  setBrandsReportAttemptLoading,
  setBrandsReportAttemptError,
  TableSort,
} from "../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { CsvSection, sortRowsForExport } from "../csvExport";
import { useOptifundTabData } from "../hooks/useOptifundTabData";
import AsyncSection from "../AsyncSection";
import {
  BRANDS_DAILY_CSV_HEADERS,
  BRANDS_MONTHLY_CSV_HEADERS,
  BRANDS_REPORT_ATTEMPT_CSV_HEADERS,
  BrandDailyRow,
  BrandMonthlyRow,
  BrandReportAttemptRow,
  mapBrandDailyApiRow,
  mapBrandMonthlyApiRow,
  mapBrandReportAttemptApiRow,
  toBrandsDailyCsvRow,
  toBrandsMonthlyCsvRow,
  toBrandsReportAttemptCsvRow,
} from "./brandsApiMapping";
import { getBrandMonthLabel, getDefaultBrandMonth, getDefaultBrandYear } from "./brandsMonthYearOptions";
import BrandsFilterCard from "./BrandsFilterCard";
import BrandsKpiCards from "./BrandsKpiCards";
import BrandsIncomeChart from "./BrandsIncomeChart";
import BrandsDailyTable from "./BrandsDailyTable";
import BrandsMonthlyTable from "./BrandsMonthlyTable";
import BrandsReportAttemptTable from "./BrandsReportAttemptTable";

const ALL_BRANDS = "All brands";
const EMPTY_DAILY_ROWS: BrandDailyRow[] = [];
const EMPTY_MONTHLY_ROWS: BrandMonthlyRow[] = [];
const EMPTY_REPORT_ATTEMPT_ROWS: BrandReportAttemptRow[] = [];
const DEFAULT_DAILY_SORT: TableSort = { keytoSort: "date", direction: "desc" };
const DEFAULT_MONTHLY_SORT: TableSort = { keytoSort: "brand", direction: "asc" };
const DEFAULT_REPORT_ATTEMPT_SORT: TableSort = { keytoSort: "brand", direction: "asc" };
const PAGE_SIZE_OPTIONS = [10, 25, 100];

interface BrandsProps {
  userToken: any;
  websiteSharedUid: string;
}

export interface BrandsExportHandle {
  getExportSections: () => CsvSection[] | null;
}

function readPage(searchParams: URLSearchParams, key: string): number {
  const value = Number(searchParams.get(key));
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

function readPageSize(searchParams: URLSearchParams, key: string): number {
  const value = Number(searchParams.get(key));
  return PAGE_SIZE_OPTIONS.includes(value) ? value : 10;
}

function readSort(searchParams: URLSearchParams, sortKey: string, dirKey: string, fallback: TableSort): TableSort {
  const key = searchParams.get(sortKey);
  const direction = searchParams.get(dirKey);
  return key && (direction === "asc" || direction === "desc") ? { keytoSort: key, direction } : fallback;
}

function readMonth(searchParams: URLSearchParams): number {
  const value = Number(searchParams.get("brands_month"));
  return Number.isInteger(value) && value >= 1 && value <= 12 ? value : getDefaultBrandMonth();
}

function readYear(searchParams: URLSearchParams): number {
  const value = Number(searchParams.get("brands_year"));
  return Number.isInteger(value) && value >= 2024 ? value : getDefaultBrandYear();
}

const Brands = forwardRef<BrandsExportHandle, BrandsProps>(function Brands(
  { userToken, websiteSharedUid },
  ref
) {
  const dispatch = useDispatch();
  const {
    brandsData,
    brandsLoading,
    brandsError,
    brandsMonthlyData,
    brandsMonthlyLoading,
    brandsMonthlyError,
    brandsReportAttemptData,
    brandsReportAttemptLoading,
    brandsReportAttemptError,
  } = useSelector((state: any) => state.optifundDashboard);

  // Filter/sort/pagination state for this tab lives here (not Redux) and is
  // synced with the URL directly via useSearchParams - see
  // docs/new-module-checklist.md Section 5/8.12.
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedBrand, setSelectedBrand] = useState(() => searchParams.get("brand") ?? ALL_BRANDS);
  const [selectedMonth, setSelectedMonth] = useState(() => readMonth(searchParams));
  const [selectedYear, setSelectedYear] = useState(() => readYear(searchParams));
  const [dailyPage, setDailyPage] = useState(() => readPage(searchParams, "brands_daily_page"));
  const [dailyRowsPerPage, setDailyRowsPerPage] = useState(() =>
    readPageSize(searchParams, "brands_daily_page_size")
  );
  const [dailySort, setDailySort] = useState<TableSort>(() =>
    readSort(searchParams, "brands_daily_sort", "brands_daily_dir", DEFAULT_DAILY_SORT)
  );
  const [monthlyPage, setMonthlyPage] = useState(() => readPage(searchParams, "brands_monthly_page"));
  const [monthlyRowsPerPage, setMonthlyRowsPerPage] = useState(() =>
    readPageSize(searchParams, "brands_monthly_page_size")
  );
  const [monthlySort, setMonthlySort] = useState<TableSort>(() =>
    readSort(searchParams, "brands_monthly_sort", "brands_monthly_dir", DEFAULT_MONTHLY_SORT)
  );
  const [reportAttemptPage, setReportAttemptPage] = useState(() => readPage(searchParams, "brands_ra_page"));
  const [reportAttemptRowsPerPage, setReportAttemptRowsPerPage] = useState(() =>
    readPageSize(searchParams, "brands_ra_page_size")
  );
  const [reportAttemptSort, setReportAttemptSort] = useState<TableSort>(() =>
    readSort(searchParams, "brands_ra_sort", "brands_ra_dir", DEFAULT_REPORT_ATTEMPT_SORT)
  );

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (selectedBrand === ALL_BRANDS) next.delete("brand");
        else next.set("brand", selectedBrand);

        if (selectedMonth === getDefaultBrandMonth()) next.delete("brands_month");
        else next.set("brands_month", String(selectedMonth));
        if (selectedYear === getDefaultBrandYear()) next.delete("brands_year");
        else next.set("brands_year", String(selectedYear));

        if (dailyPage === 0) next.delete("brands_daily_page");
        else next.set("brands_daily_page", String(dailyPage));
        if (dailyRowsPerPage === 10) next.delete("brands_daily_page_size");
        else next.set("brands_daily_page_size", String(dailyRowsPerPage));
        if (dailySort.keytoSort === DEFAULT_DAILY_SORT.keytoSort && dailySort.direction === DEFAULT_DAILY_SORT.direction) {
          next.delete("brands_daily_sort");
          next.delete("brands_daily_dir");
        } else {
          next.set("brands_daily_sort", dailySort.keytoSort);
          next.set("brands_daily_dir", dailySort.direction);
        }

        if (monthlyPage === 0) next.delete("brands_monthly_page");
        else next.set("brands_monthly_page", String(monthlyPage));
        if (monthlyRowsPerPage === 10) next.delete("brands_monthly_page_size");
        else next.set("brands_monthly_page_size", String(monthlyRowsPerPage));
        if (
          monthlySort.keytoSort === DEFAULT_MONTHLY_SORT.keytoSort &&
          monthlySort.direction === DEFAULT_MONTHLY_SORT.direction
        ) {
          next.delete("brands_monthly_sort");
          next.delete("brands_monthly_dir");
        } else {
          next.set("brands_monthly_sort", monthlySort.keytoSort);
          next.set("brands_monthly_dir", monthlySort.direction);
        }

        if (reportAttemptPage === 0) next.delete("brands_ra_page");
        else next.set("brands_ra_page", String(reportAttemptPage));
        if (reportAttemptRowsPerPage === 10) next.delete("brands_ra_page_size");
        else next.set("brands_ra_page_size", String(reportAttemptRowsPerPage));
        if (
          reportAttemptSort.keytoSort === DEFAULT_REPORT_ATTEMPT_SORT.keytoSort &&
          reportAttemptSort.direction === DEFAULT_REPORT_ATTEMPT_SORT.direction
        ) {
          next.delete("brands_ra_sort");
          next.delete("brands_ra_dir");
        } else {
          next.set("brands_ra_sort", reportAttemptSort.keytoSort);
          next.set("brands_ra_dir", reportAttemptSort.direction);
        }
        return next;
      },
      { replace: true }
    );
  }, [
    selectedBrand,
    selectedMonth,
    selectedYear,
    dailyPage,
    dailyRowsPerPage,
    dailySort,
    monthlyPage,
    monthlyRowsPerPage,
    monthlySort,
    reportAttemptPage,
    reportAttemptRowsPerPage,
    reportAttemptSort,
    setSearchParams,
  ]);

  const handleDailyRowsPerPageChange = (nextRowsPerPage: number) => {
    setDailyRowsPerPage(nextRowsPerPage);
    setDailyPage(0);
  };

  const handleMonthlyRowsPerPageChange = (nextRowsPerPage: number) => {
    setMonthlyRowsPerPage(nextRowsPerPage);
    setMonthlyPage(0);
  };

  const handleReportAttemptRowsPerPageChange = (nextRowsPerPage: number) => {
    setReportAttemptRowsPerPage(nextRowsPerPage);
    setReportAttemptPage(0);
  };

  const handleSelectedMonthChange = (month: number) => {
    setSelectedMonth(month);
    setMonthlyPage(0);
    setReportAttemptPage(0);
  };

  const handleSelectedYearChange = (year: number) => {
    setSelectedYear(year);
    setMonthlyPage(0);
    setReportAttemptPage(0);
  };

  // The Daily/Last-30-Days query isn't date/month based (spec Section 2), so
  // it never takes month/year - only brand. Brand itself requires the
  // `brand` param same as website_shared_uid - always fetching "all" here
  // and filtering client-side (below) keeps brand-switching instant and lets
  // the filter dropdown's options be derived from the real brand names in
  // the response. Each query tracks its own error + exposes its own
  // `refetch` so one table's failure/retry never touches the other two.
  const { refetch: refetchDaily } = useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsDailyId,
    (data) => dispatch(setBrandsData(data)),
    (loading) => dispatch(setBrandsLoading(loading)),
    undefined,
    undefined,
    "all",
    undefined,
    undefined,
    (hasError) => dispatch(setBrandsError(hasError))
  );

  // Monthly and Report Attempt queries are both selected by Month/Year
  // (spec Section 2/3) - they replace the old MTD query and add the new
  // reporting-attempt table respectively.
  const { refetch: refetchMonthly } = useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsMonthlyId,
    (data) => dispatch(setBrandsMonthlyData(data)),
    (loading) => dispatch(setBrandsMonthlyLoading(loading)),
    undefined,
    undefined,
    "all",
    selectedMonth,
    selectedYear,
    (hasError) => dispatch(setBrandsMonthlyError(hasError))
  );

  const { refetch: refetchReportAttempt } = useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsReportAttemptId,
    (data) => dispatch(setBrandsReportAttemptData(data)),
    (loading) => dispatch(setBrandsReportAttemptLoading(loading)),
    undefined,
    undefined,
    "all",
    selectedMonth,
    selectedYear,
    (hasError) => dispatch(setBrandsReportAttemptError(hasError))
  );

  const dailyRows: BrandDailyRow[] = useMemo(
    () => (Array.isArray(brandsData) ? brandsData.map(mapBrandDailyApiRow) : EMPTY_DAILY_ROWS),
    [brandsData]
  );

  const monthlyRows: BrandMonthlyRow[] = useMemo(
    () => (Array.isArray(brandsMonthlyData) ? brandsMonthlyData.map(mapBrandMonthlyApiRow) : EMPTY_MONTHLY_ROWS),
    [brandsMonthlyData]
  );

  const reportAttemptRows: BrandReportAttemptRow[] = useMemo(
    () =>
      Array.isArray(brandsReportAttemptData)
        ? brandsReportAttemptData.map(mapBrandReportAttemptApiRow)
        : EMPTY_REPORT_ATTEMPT_ROWS,
    [brandsReportAttemptData]
  );

  const brandOptions = useMemo(() => {
    const uniqueBrands = Array.from(
      new Set([...dailyRows.map((row) => row.brand), ...monthlyRows.map((row) => row.brand)])
    ).sort();
    return [ALL_BRANDS, ...uniqueBrands];
  }, [dailyRows, monthlyRows]);

  const filteredDailyRows = useMemo(
    () => dailyRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [dailyRows, selectedBrand]
  );

  const filteredMonthlyRows = useMemo(
    () => monthlyRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [monthlyRows, selectedBrand]
  );

  const filteredReportAttemptRows = useMemo(
    () => reportAttemptRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [reportAttemptRows, selectedBrand]
  );

  const monthlyPeriodLabel = `${getBrandMonthLabel(selectedMonth)} ${selectedYear}`;

  // Each of the three queries is its own independent section: it shows a
  // skeleton only until ITS OWN data resolves, an error+retry state if it
  // failed, or its content - so a slow/failed query never blocks (or takes
  // down) the other two, and changing Month/Year doesn't reset the whole
  // tab back to one big skeleton.
  const dailyReady = Array.isArray(brandsData);
  const monthlyReady = Array.isArray(brandsMonthlyData);
  const reportAttemptReady = Array.isArray(brandsReportAttemptData);
  const dailySectionLoading = brandsLoading || (!dailyReady && !brandsError);
  const monthlySectionLoading = brandsMonthlyLoading || (!monthlyReady && !brandsMonthlyError);
  const reportAttemptSectionLoading = brandsReportAttemptLoading || (!reportAttemptReady && !brandsReportAttemptError);

  const isFullyLoaded =
    dailyReady && !brandsError && monthlyReady && !brandsMonthlyError && reportAttemptReady && !brandsReportAttemptError;

  useImperativeHandle(
    ref,
    () => ({
      getExportSections: (): CsvSection[] | null => {
        if (!isFullyLoaded) return null;
        return [
          {
            title: "Brands - Monthly",
            headers: BRANDS_MONTHLY_CSV_HEADERS,
            rows: sortRowsForExport(filteredMonthlyRows, monthlySort).map(toBrandsMonthlyCsvRow),
          },
          {
            title: "Brands - Reporting Attempt Analysis",
            headers: BRANDS_REPORT_ATTEMPT_CSV_HEADERS,
            rows: sortRowsForExport(filteredReportAttemptRows, reportAttemptSort).map(toBrandsReportAttemptCsvRow),
          },
          {
            title: "Brands - Daily",
            headers: BRANDS_DAILY_CSV_HEADERS,
            rows: sortRowsForExport(filteredDailyRows, dailySort).map(toBrandsDailyCsvRow),
          },
        ];
      },
    }),
    [
      isFullyLoaded,
      filteredDailyRows,
      filteredMonthlyRows,
      filteredReportAttemptRows,
      dailySort,
      monthlySort,
      reportAttemptSort,
    ]
  );

  return (
    <Stack spacing={2.5}>
      <BrandsFilterCard
        brandOptions={brandOptions}
        selectedBrand={selectedBrand}
        onSelectedBrandChange={setSelectedBrand}
        selectedMonth={selectedMonth}
        onSelectedMonthChange={handleSelectedMonthChange}
        selectedYear={selectedYear}
        onSelectedYearChange={handleSelectedYearChange}
        disableMonthYear={brandsMonthlyLoading || brandsReportAttemptLoading}
      />

      <AsyncSection
        isLoading={monthlySectionLoading}
        isError={brandsMonthlyError}
        onRetry={refetchMonthly}
        skeletonHeight={640}
        errorMessage="Couldn't load the Brands Performance - Monthly data."
      >
        <Stack spacing={2.5}>
          <BrandsKpiCards rows={filteredMonthlyRows} />
          <BrandsIncomeChart rows={filteredMonthlyRows} periodLabel={monthlyPeriodLabel} />
          <BrandsMonthlyTable
            rows={filteredMonthlyRows}
            periodLabel={monthlyPeriodLabel}
            page={monthlyPage}
            onPageChange={setMonthlyPage}
            rowsPerPage={monthlyRowsPerPage}
            onRowsPerPageChange={handleMonthlyRowsPerPageChange}
            sort={monthlySort}
            onSortChange={setMonthlySort}
          />
        </Stack>
      </AsyncSection>

      <AsyncSection
        isLoading={reportAttemptSectionLoading}
        isError={brandsReportAttemptError}
        onRetry={refetchReportAttempt}
        skeletonHeight={320}
        errorMessage="Couldn't load the Brand Reporting Attempt Analysis."
      >
        <BrandsReportAttemptTable
          rows={filteredReportAttemptRows}
          periodLabel={monthlyPeriodLabel}
          page={reportAttemptPage}
          onPageChange={setReportAttemptPage}
          rowsPerPage={reportAttemptRowsPerPage}
          onRowsPerPageChange={handleReportAttemptRowsPerPageChange}
          sort={reportAttemptSort}
          onSortChange={setReportAttemptSort}
        />
      </AsyncSection>

      <AsyncSection
        isLoading={dailySectionLoading}
        isError={brandsError}
        onRetry={refetchDaily}
        skeletonHeight={320}
        errorMessage="Couldn't load the Brands Performance - Daily data."
      >
        <BrandsDailyTable
          rows={filteredDailyRows}
          page={dailyPage}
          onPageChange={setDailyPage}
          rowsPerPage={dailyRowsPerPage}
          onRowsPerPageChange={handleDailyRowsPerPageChange}
          sort={dailySort}
          onSortChange={setDailySort}
        />
      </AsyncSection>
    </Stack>
  );
});

export default Brands;
